import { type DocumentParseState } from '@units/document/types/document.types.js';

/**
 * Насколько разобрана последняя редакция документа — словарь `document_parsing_reports.status`,
 * а не второй, придуманный для экрана.
 *
 * Перечень выписан кортежем, потому что это ещё и **порядок фишек фильтра**: сперва разобранные,
 * затем разобранные с замечаниями, затем неразобранные — от штатного хода к тому, ради чего фильтр
 * и открывают. `satisfies` сверяет каждое значение с контрактом, а `Record<DocumentParseState, …>`
 * ниже — полноту: состояние, добавленное на сервере, становится ошибкой сборки здесь, а не
 * пустой фишкой в интерфейсе.
 */
export const DOCUMENT_PARSE_STATES = [
  'PARSED',
  'PARSED_WITH_NOTICES',
  'NOT_PARSED',
] as const satisfies readonly DocumentParseState[];

/**
 * Ключи, а не подписи (`rules/i18n.mdc` §5): текст берётся `t()` в компоненте.
 *
 * Выписаны поштучно, а не собраны как `documents.parseState.${state}`: ключ, собранный в рантайме,
 * не виден гейту паритета словарей, и пропущенный перевод доезжает до юриста строкой
 * `documents.parseState.NOT_PARSED` вместо «не разобран» (ADR-0019).
 */
export const DOCUMENT_PARSE_STATE_LABEL: Readonly<Record<DocumentParseState, string>> = {
  PARSED: 'documents.parseState.PARSED',
  PARSED_WITH_NOTICES: 'documents.parseState.PARSED_WITH_NOTICES',
  NOT_PARSED: 'documents.parseState.NOT_PARSED',
};

/**
 * Роль в палитре, а не оттенок (`rules/design-system.mdc` §3, `rules/a11y.mdc` §1).
 *
 * `NOT_PARSED` — `danger`, а не `warning`, и это не косметика: о таком документе **нельзя
 * спросить**, тогда как разобранный с замечаниями отвечает, просто требует взгляда. Раньше оба
 * состояния рисовались одним предупреждающим цветом, то есть авария была нарисована ровно так же,
 * как замечание. Цвет при этом никогда не единственный носитель смысла — рядом стоит текст метки.
 *
 * `PARSED` — `neutral`, а не `success`, и это видно только на длинном перечне. Метка стоит у
 * **каждого** документа намеренно (`DocumentParseBadge`), но штатный случай — это двадцать восемь
 * строк из тридцати: зелёным они превращаются в самое яркое пятно экрана и перекрикивают
 * наименование документа, ради которого реестр открывают. Разбор — не достижение, а норма; цветом
 * выделяется отклонение от неё, а норма просто названа словом.
 */
export const DOCUMENT_PARSE_STATE_TONE: Readonly<
  Record<DocumentParseState, 'neutral' | 'warning' | 'danger'>
> = {
  PARSED: 'neutral',
  PARSED_WITH_NOTICES: 'warning',
  NOT_PARSED: 'danger',
};

/** Пришло ли с сервера то, что реестр умеет показать: whitelist для параметра из адресной строки. */
export const isDocumentParseState = (value: unknown): value is DocumentParseState =>
  typeof value === 'string' && (DOCUMENT_PARSE_STATES as readonly string[]).includes(value);
